/**
 * reportClientError — ship a crash to the backend so it lands in admin App Logs.
 *
 * Written to work when the app is already broken, which drives every choice
 * here: no imports from the api client (it pulls in Amplify, which may itself
 * be the thing that failed), no auth requirement, and no thrown errors on any
 * path. A reporter that can crash is worse than no reporter.
 *
 * Uses sendBeacon when available so a report still leaves the page during a
 * reload or a backgrounding — the exact moments a white-screen user triggers.
 */

const ENDPOINT = `${process.env.REACT_APP_API_URL || ''}/api/client-errors`;
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

// A crash inside a render loop can fire hundreds of times a second. The server
// dedupes and rate-limits, but there is no reason to make it defend itself —
// send each distinct signature once per session.
const seen = new Set();
const MAX_DISTINCT = 20;

export function reportClientError(kind, error, extra = {}) {
  try {
    const message = String(error?.message || error || 'Unknown error').slice(0, 1000);
    const stack   = error?.stack ? String(error.stack).slice(0, 8000) : undefined;

    const key = `${kind}|${message}|${(stack || '').split('\n')[1] || ''}`;
    if (seen.has(key) || seen.size >= MAX_DISTINCT) return;
    seen.add(key);

    const body = JSON.stringify({
      kind, message, stack,
      componentStack: extra.componentStack ? String(extra.componentStack).slice(0, 8000) : undefined,
      path:       `${window.location.pathname}${window.location.search}`,
      userAgent:  navigator.userAgent,
      appVersion: APP_VERSION,
    });

    // sendBeacon survives the page going away; fetch with keepalive is the
    // fallback. Both are fire-and-forget — we never surface a reporting
    // failure to a user who is already looking at a broken screen.
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch { /* never let the reporter throw */ }
}

/**
 * Catch errors that escape React entirely — event handlers, async callbacks,
 * rejected promises. An ErrorBoundary sees none of these, and they are a common
 * cause of a page that looks frozen rather than crashed.
 *
 * Registered once from index.js, before the app mounts.
 */
export function installGlobalErrorReporting() {
  window.addEventListener('error', (event) => {
    // Failed <script>/<img> loads also fire 'error' but have no `error` object.
    // A missing JS chunk is worth knowing about — it is what a stale cached
    // index.html looks like from the browser's side.
    if (!event.error && event.target && event.target !== window) {
      const src = event.target.src || event.target.href;
      if (src) reportClientError('window', new Error(`Failed to load resource: ${src}`));
      return;
    }
    reportClientError('window', event.error || new Error(event.message));
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    reportClientError('unhandledrejection', event.reason);
  });
}
